import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { useElevenLabs } from "../hooks/useElevenLabs.ts";
// Components
import SuccessScreen from "../layout/SuccessScreen.tsx";
import WelcomeScreen from "../layout/Interview/WelcomeScreen.tsx";
import ProfileScreen from "../layout/Interview/ProfileScreen.tsx";
import InterviewMainScreen from "../layout/Interview/InterviewMainScreen.tsx";
import QuestionsSidebar from "../layout/Interview/QuestionsSidebar.tsx";
import FinalSuccessScreen from "../layout/Interview/FinalSuccessScreen.tsx";
import SummaryReviewModal from "../model/SummaryReviewModal.tsx";
import { getToken } from "../auth/authConfig.jsx";
import { use } from "react";

const WelcomeInterview = () => {
  const [currentStep, setCurrentStep] = useState("welcome");
  const [progress, setProgress] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [speechRecognition, setSpeechRecognition] = useState(null);
  const [isFollowup, setIsFollowup] = useState(true);
  const [currentInterviewData, setCurrentInterviewData] = useState(null);
  const [loadingNextQuestion, setLoadingNextQuestion] = useState(false);
  const [interviewComplete, setInterviewComplete] = useState(false);
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [phase, setPhase] = useState("phase1");
  const [confirming, setConfirming] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [isFinalPhase, setIsFinalPhase] = useState(false);
  const [phaseSummaries, setPhaseSummaries] = useState([]);
  const [showSummaryReview, setShowSummaryReview] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [allPhasesComplete, setAllPhasesComplete] = useState(false);
  const [isLoadingQuestionWithAudio, setIsLoadingQuestionWithAudio] = useState(false);
  const [profileSummary, setProfileSummary] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // Track previous question data for API payload
  const [previousQuestionData, setPreviousQuestionData] = useState({
    counter_question:null,
    counter_question_number: null,
    main_question: '',
    main_question_number: 1,

  });

  useEffect(() => {
    console.log("Previous Question Data updated:", previousQuestionData);
  },[previousQuestionData])
  console.log("WelcomeInterview component initialized",previousQuestionData);

  // Store the latest interview data to maintain progress consistency
  const [latestInterviewData, setLatestInterviewData] = useState(null);

  const { accounts } = useMsal();
  const account = accounts?.[0];
  const name = account?.name || "User";
  
  const [profileData, setProfileData] = useState({
    role: "Senior Financial Analyst",
    tenure: "3 years",
    coreTasks: [
      "Monthly financial reporting and analysis",
      "Budget preparation and variance analysis",
      "Cash flow forecasting",
      "Financial modeling and projections",
    ],
  });

  console.log("WelcomeInterview component initialized with profile data:", isFinalPhase);

  const [fieldToEdit, setFieldToEdit] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionsAndResponses, setQuestionsAndResponses] = useState([]);

  const elevenLabs = useElevenLabs({
    apiKey: process.env.REACT_APP_ELEVENLABS_API_KEY,
    voiceId: 'UgBBYS2sOqTuMpoF3BR0'
  });

  // Get session ID from URL
  const params = new URLSearchParams(window.location.search);
  const sessionIdFromUrl = params.get("id");

  // Fallback function using browser's built-in TTS
  const fallbackSpeakQuestion = useCallback((text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.8;
      utterance.pitch = 1;
      utterance.volume = 1;
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  const handleStartInterview = async () => {
    setCurrentStep("profile");
    setProgress(25);
    
    // Fetch profile summary when moving to profile step
    await fetchProfileSummary();
  };

  const fetchProfileSummary = async () => {
    try {
      setProfileLoading(true);
      
      const baseurl = `https://aiktpybackend-bwfaenf2ajdufbgj.eastus-01.azurewebsites.net/pre_profile_summary`;
      const payload = {
        email: localStorage.getItem('myemail') || 'gaurava@aaysinsight.com',
        employee_name: localStorage.getItem('userName') || 'Gaurav Agarwal',
        employee_id: localStorage.getItem('empid') || '001',
      };

      const response = await fetch(baseurl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch profile summary");
      }

      const data = await response.json();
      console.log("Profile Summary:", data);
      setProfileSummary(data);
      setProfileLoading(false);
    } catch (error) {
      console.error("Error fetching profile summary:", error);
      setProfileLoading(false);
      // Continue without profile summary if it fails
    }
  };

  const handleProfileConfirm = async () => {
    try {
      await fetchSessionId();
    } catch (error) {
      console.error("Error starting interview:", error);
    }
  };

  const handleEditField = (field, value) => {
    setFieldToEdit(field);
    setEditValue(value);
    setIsEditModalOpen(true);
  };

  const handleAnswerChange = (questionId, answer) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === questionId ? { ...q, answer } : q))
    );

    const currentQuestion = questions.find((q) => q.id === questionId);

    setQuestionsAndResponses((prev) => {
      const existingIndex = prev.findIndex((q) => q.id === questionId);

      if (existingIndex >= 0) {
        return prev.map((q) => (q.id === questionId ? { ...q, answer } : q));
      } else if (currentQuestion) {
        return [...prev, { ...currentQuestion, answer }];
      }

      return prev;
    });
  };

  const handleDocumentUpload = (questionId, file) => {
    setUploadedDocuments(prev => ({
      ...prev,
      [questionId]: [...(prev[questionId] || []), file]
    }));
  };

  const handleDocumentRemove = (questionId, fileIndex) => {
    setUploadedDocuments(prev => ({
      ...prev,
      [questionId]: prev[questionId]?.filter((_, index) => index !== fileIndex) || []
    }));
  };

  const toggleQuestionCollapse = (id) => {
    setQuestionsAndResponses((prev) =>
      prev.map((q) => (q.id === id ? { ...q, isCollapsed: !q.isCollapsed } : q))
    );
  };

  const handleNextQuestion = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    if (
      currentQuestion &&
      currentQuestion.answer &&
      currentQuestion.answer.trim() !== ""
    ) {
      await submitAnswerAndGetNext(currentQuestion);
    } else {
      alert("Please provide an answer before proceeding to the next question.");
    }
  };

  const handleQuestionClick = (questionId) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    if (questionIndex !== -1) {
      setCurrentQuestionIndex(questionIndex);
      
      // Don't reset currentInterviewData when clicking on sidebar questions
      // Keep the latest interview data for consistent progress display
      console.log("🔄 Question clicked - maintaining latest interview data for progress");
    }
  };

  const handleGenerateSummary = () => {
    setCurrentStep("success");
    setProgress(100);
  };

  const handleSuccessScreenAction = (isFinal) => {
    setShowSuccessScreen(true);
    setIsFinalPhase(isFinal);
    if (isFinal) {
      setAllPhasesComplete(true);
    }
  };

  const handleContinueToNextPhase = async () => {
    setShowSuccessScreen(false);
    setConfirming(false);
    setInterviewComplete(false);
    setIsFollowup(true);
    setSummary("");
    setQuestions([]);
    setQuestionsAndResponses([]);
    setCurrentQuestionIndex(0);
    setPreviousQuestionData(null);
    setLatestInterviewData(null);
    
    await InterViewQuestions(sessionId, true, true);
  };

  const handleViewSummaryFromSuccess = () => {
    setShowSuccessScreen(false);
  };

  const handleViewAllSummaries = () => {
    setShowSummaryReview(true);
  };

  const handleCloseSummaryReview = () => {
    setShowSummaryReview(false);
    if (allPhasesComplete) {
      setShowSuccessScreen(true);
    }
  };

  const toggleRecording = () => {
    if (!isRecording) {
      startSpeechRecognition();
    } else {
      stopSpeechRecognition();
    }
  };

  const startSpeechRecognition = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert(
        "Speech recognition is not supported in your browser. Please try Chrome, Edge, or Safari."
      );
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    let finalTranscript = "";

    recognition.onstart = () => {
      setIsRecording(true);
      console.log("Speech recognition started");
    };

    recognition.onresult = (event) => {
      let interimTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalTranscript += transcript + " ";
        } else {
          interimTranscript += transcript;
        }
      }

      const currentQuestion = questions[currentQuestionIndex];
      const currentAnswer = currentQuestion?.answer || "";

      if (currentQuestion) {
        handleAnswerChange(currentQuestion.id, currentAnswer + finalTranscript);
      }

      if (finalTranscript) {
        finalTranscript = "";
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setIsRecording(false);

      let errorMessage = "Speech recognition error: ";
      switch (event.error) {
        case "no-speech":
          errorMessage += "No speech was detected. Please try again.";
          break;
        case "audio-capture":
          errorMessage +=
            "No microphone was found. Please check your microphone settings.";
          break;
        case "not-allowed":
          errorMessage +=
            "Microphone access was denied. Please allow microphone access and try again.";
          break;
        case "network":
          errorMessage +=
            "Network error occurred. Please check your internet connection.";
          break;
        default:
          errorMessage += event.error;
      }
      alert(errorMessage);
    };

    recognition.onend = () => {
      setIsRecording(false);
      setSpeechRecognition(null);
      console.log("Speech recognition ended");
    };

    recognition.start();
    setSpeechRecognition(recognition);
  };

  const stopSpeechRecognition = () => {
    if (speechRecognition) {
      speechRecognition.stop();
      setSpeechRecognition(null);
    }
    setIsRecording(false);
  };

  const fetchSessionId = async () => {
    try {
      setSessionLoading(true);

      const baseurl = `https://aiktpybackend-bwfaenf2ajdufbgj.eastus-01.azurewebsites.net/start_interview`;
      const payload = {
        email: "nabasundarr@aaysinsight.com",
        employee_name: "Nabasundar Rout",
        employee_id: "001",
      };

      const response = await fetch(baseurl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to create session");
      }

      const data = await response.json();
      console.log("Session ID:", data.session_id);
      setSessionId(data.session_id);

      // ✅ Update URL with session ID (without page reload)
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("id", data.session_id);
      window.history.pushState({}, "", newUrl.toString());

      // For the first question, we only need session_id
      // await InterViewQuestions(data.session_id, true, true);

      setCurrentStep("interview");
      setProgress(50);
      setSessionLoading(false);
    } catch (error) {
      console.error("Error fetching session ID:", error);
      setSessionLoading(false);
      alert("Failed to start interview session. Please try again later.");
    }
  };

  const saveHistory = async (data,payload) => {
    try {
    
      if(payload.user_response.trim() === ""){
       return false
      }
        

      const dataitem = {
        data: data,
        payload: payload,
      }
      const url = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/historysave`;
      const token = await getToken();
      const response = await axios.post(url, dataitem, {
        headers: {
          "Content-Type": "application/json",
          "oidc-token": token
        }
      });
  
      console.log("✅ Response:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Error saving history:", error.response?.data || error.message);
      return { error: error.message };
    }
  };

  const InterViewQuestions = useCallback(
    async (sessionIdParam, isFollowUpParam, isFirstQuestion = false) => {
      console.log("🚀 Starting sequential API flow...", isFirstQuestion, previousQuestionData);
      
      try {
        const currentSessionId = sessionIdParam || sessionId || sessionIdFromUrl;
        if (!currentSessionId) {
          console.error("No session ID available");
          return;
        }

        setIsLoadingQuestionWithAudio(true);
        console.log("⏳ Loading state activated - waiting for question and audio...");

        elevenLabs.stop();

        console.log("📞 Step 1: Calling next_question API...");
        
        // Build payload based on whether it's the first question or not
        let payload = {
          session_id: currentSessionId,
        };

        console.log("currentQuestion?.answer",currentQuestion?.answer, previousQuestionData, isFirstQuestion);
        // If not the first question, include previous question data and user response
        const userAnswer = currentQuestion?.answer || "";

        if (!isFirstQuestion) {
          payload = {
            ...payload,
            previous_main_question_number: previousQuestionData.main_question_number || 0,
            previous_main_question: previousQuestionData.main_question || "",
            previous_counter_question_number: previousQuestionData.counter_question_number || 0,
            previous_counter_question: previousQuestionData.counter_question || "",
            ...(userAnswer && { user_response: userAnswer }) // 👈 conditionally include only if userAnswer is truthy

          };
        }
        

        console.log("📤 Payload:", payload);

        const baseurl = `https://aiktpybackend-bwfaenf2ajdufbgj.eastus-01.azurewebsites.net/next_question`;

        // Add timeout and retry logic for API calls
        const response = await axios.post(baseurl, payload, {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 60000, // 60 second timeout
        });

        const data = response.data;
        console.log("✅ Step 1 Complete: Interview API response received");
        
        if (!isFirstQuestion && previousQuestionData) {
          saveHistory(data,payload);
        }
        
        // Determine which question to use - counter_question takes priority
        const questionText = data.counter_question || data.main_question;
        const questionNumber = data.counter_question_number || data.main_question_number;
        const isCounterQuestion = !!data.counter_question;

        if (data.is_interview_complete === true) {
          setIsFinalPhase(true);
        }
        
        console.log("📝 Step 2: Question received:", questionText || "No question");
        console.log("🔢 Question Number:", questionNumber, isCounterQuestion ? "(Counter)" : "(Main)");

        // Update both current and latest interview data
        setCurrentInterviewData(data);
        setLatestInterviewData(data);

        // Store current question data for next API call
        setPreviousQuestionData({
          main_question_number: data.main_question_number,
          main_question: data.main_question,
          counter_question_number: data.counter_question_number,
          counter_question: data.counter_question
        });

        // Check if interview is complete
        if (data.is_interview_complete) {
          console.log("🏁 Interview complete");
          setIsFollowup(false);
          setInterviewComplete(true);
          setIsLoadingQuestionWithAudio(false);
          return;
        }

        setIsFollowup(!data.is_interview_complete);
        setInterviewComplete(false);

        console.log("🔊 Step 3: Starting ElevenLabs API call...");
        
        if (questionText) {
          try {
            await elevenLabs.speak(questionText, {
              stability: 0.5,
              similarityBoost: 0.8,
              style: 0.2,
              useSpeakerBoost: true
            });
            console.log("✅ Step 3 Complete: Audio loaded and playing");
          } catch (audioError) {
            console.error('Audio failed, but continuing with question display:', audioError);
            fallbackSpeakQuestion(questionText);
          }
        }

        console.log("📋 Step 4: Adding question to DOM...");

        // Create question object from new API response structure
        const newQuestion = {
          id: `${isCounterQuestion ? 'counter' : 'main'}_${questionNumber}_${Date.now()}`,
          question: questionText || "",
          answer: "",
          isCollapsed: true,
          field: data.interview_phase,
          type: isCounterQuestion ? "counter" : "main",
          questionNumber: questionNumber,
          isCounterQuestion: isCounterQuestion,
        };

        // Add new question to questions array
        setQuestions((prev) => {
          const newQuestions = [...prev, newQuestion];
          setCurrentQuestionIndex(newQuestions.length - 1);
          return newQuestions;
        });

        // Always add to Questions & Responses sidebar
        setQuestionsAndResponses((prev) => {
          // Check if this exact question already exists
          const exists = prev.some((q) => q.id === newQuestion.id);
          
          if (!exists) {
            return [...prev, { ...newQuestion, isCollapsed: true }];
          }
          
          return prev;
        });

        setIsLoadingQuestionWithAudio(false);
        console.log("✅ Step 4 Complete: Question displayed in DOM with audio ready");

      } catch (error) {
        console.error("❌ Error in sequential API flow:", error);
        setIsLoadingQuestionWithAudio(false);
        
        // Handle specific error types
        if (error.code === 'ECONNABORTED' || error.response?.status === 499) {
          alert("The request is taking longer than expected. Please try again or check your connection.");
        } else {
          alert("Failed to load interview questions. Please try again later.");
        }
      }
    },
    [sessionId, isFollowup, elevenLabs, fallbackSpeakQuestion, previousQuestionData, questions, currentQuestionIndex]
  );

  const submitAnswerAndGetNext = async (currentQuestion) => {
    try {
      setLoadingNextQuestion(true);
      
      elevenLabs.stop();
      
      // Call next question with previous question data and user response
      await InterViewQuestions(sessionId, isFollowup, false);
    } catch (error) {
      console.error("Error submitting answer:", error);
      alert("Failed to submit answer. Please try again.");
    } finally {
      setLoadingNextQuestion(false);
    }
  };

  const generateSummary = async () => {
    setSummaryLoading(true);
  
    try {
      if (!sessionId) {
        console.error("No session ID available for summary generation");
        alert("Session ID is missing. Cannot generate summary.");
        setSummaryLoading(false);
        return;
      }
  
      const response = await axios.post(
        "https://aiktpybackend-bwfaenf2ajdufbgj.eastus-01.azurewebsites.net/post_interview_summary",
        { session_id: sessionId },
      );
  
      if (response.data && response.data.summary) {
        setSummary(response.data.summary); // assuming the response has { summary: "..." }
      } else {
        setSummary("No summary returned.");
      }
  
    } catch (error) {
      console.error("❌ Error generating summary:", error.response?.data || error.message);
      alert("Failed to generate summary. Please try again later.");
      setSummary("Error generating summary.");
    } finally {
      setSummaryLoading(false);
    }
  };

  const returningForInterview = async () => {
    setConfirming(false);
    await InterViewQuestions(sessionId, true, false);
  };

  const resetInterview = () => {
    elevenLabs.stop();
    
    setCurrentStep("welcome");
    setProgress(0);
    setCurrentQuestionIndex(0);
    setQuestionsAndResponses([]);
    setQuestions([]);
    setSessionId(null);
    setCurrentInterviewData(null);
    setLatestInterviewData(null);
    setIsFollowup(true);
    setInterviewComplete(false);
    setLoadingNextQuestion(false);
    setShowSuccessScreen(false);
    setIsFinalPhase(false);
    setConfirming(false);
    setSummary("");
    setPhase("phase1");
    setPhaseSummaries([]);
    setAllPhasesComplete(false);
    setIsLoadingQuestionWithAudio(false);
    setPreviousQuestionData(null);
    setProfileSummary(null);
  };

  const formatPhase = (phaseString) => {
    if (!phaseString) return '';
    const [phase, ...rest] = phaseString.split('_');
    const label = rest.join(' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return `${phase.charAt(0).toUpperCase() + phase.slice(1)}: ${label}`;
  };

  const handlegetDatafromsession = useCallback(async (sessionId) => {
    try {
      if (!sessionId) {
        console.warn("No session ID found in URL");
        return;
      }

      setSessionLoading(true);
      const baseurl = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/latest_question?session_id=${sessionId}`;
      const token = await getToken();

      const response = await axios.get(baseurl, {
        headers: { "oidc-token": token },
      });

      const data = response.data;
      console.log("Session Data:", data);

      // Proceed only if data exists
      if (!data) {
        throw new Error("No session data returned");
      }

      const dataItem = data && data.data; // Handle both array and object responses
      console.log("Data Item:", dataItem);
      if (response.status === 200) {
        setPreviousQuestionData({
          main_question_number: dataItem.main_question_number,
          main_question: dataItem.main_question,
          counter_question_number: dataItem.counter_question_number,
          counter_question: dataItem.counter_question
        });

        // Wait for state to update before calling InterViewQuestions
        InterViewQuestions(sessionId, true);

        setQuestionsAndResponses(data.history || []);

      }
      
      if (response.status === 202) {
        InterViewQuestions(sessionId, true);
      }

      // Set states
      setSessionId(sessionId);
      setCurrentStep("interview");
      setLatestInterviewData(data);
      setQuestions(data.questions || []);
      setQuestionsAndResponses(data.history || []);
    } catch (error) {
      console.error("Error fetching data from session:", error);
    } finally {
      setSessionLoading(false);
    }
  }, [InterViewQuestions, getToken]);

  useEffect(() => {
    if (sessionIdFromUrl) {
      console.log("Session ID found in URL, setting state...");
      setCurrentStep("interview");
      handlegetDatafromsession(sessionIdFromUrl);
    }
  }, [sessionIdFromUrl]);

  useEffect(() => {
    console.log("State updated:", {
      sessionId,
      isFollowup,
      interviewComplete,
      questionsLength: questions.length,
      questionsAndResponsesLength: questionsAndResponses.length,
      currentQuestionIndex,
      isLoadingQuestionWithAudio,
      previousQuestionData,
      latestInterviewData: latestInterviewData ? "Available" : "None",
    });
  }, [
    sessionId,
    currentInterviewData,
    latestInterviewData,
    isFollowup,
    questions,
    questionsAndResponses,
    interviewComplete,
    currentQuestionIndex,
    isLoadingQuestionWithAudio,
    previousQuestionData,
  ]);

  const currentQuestion = questions[currentQuestionIndex];
  const phaseData = currentInterviewData;
  const answeredQuestions = questions.filter(
    (q) => q.answer && q.answer.trim() !== ""
  ).length;

  const handleSummaryGenerated = (summaryData, newPhase) => {
    const summaryWithQuestionCount = {
      ...summaryData,
      questionsAnswered: answeredQuestions,
      phase: formatPhase(phaseData?.interview_phase || "")
    };
    
    setPhaseSummaries(prev => [...prev, summaryWithQuestionCount]);
  };

  // Show success screen when confirming is true
  if (showSuccessScreen) {
    return (
      <SuccessScreen
        phase={formatPhase(phaseData?.interview_phase || "")}
        onContinueToNextPhase={handleContinueToNextPhase}
        onViewSummary={handleViewSummaryFromSuccess}
        isFinalPhase={isFinalPhase}
        questionsAnswered={answeredQuestions}
        onStartNewInterview={resetInterview}
        onViewAllSummaries={handleViewAllSummaries}
      />
    );
  }

  if (currentStep === "welcome") {
    return (
      <WelcomeScreen
        name={name}
        progress={progress}
        onStartInterview={handleStartInterview}
      />
    );
  }

  if (currentStep === "profile") {
    return (
      <ProfileScreen
        profileData={profileData}
        profileSummary={profileSummary}
        profileLoading={profileLoading}
        sessionLoading={sessionLoading}
        isEditModalOpen={isEditModalOpen}
        fieldToEdit={fieldToEdit}
        editValue={editValue}
        onProfileConfirm={handleProfileConfirm}
        onEditField={handleEditField}
        setIsEditModalOpen={setIsEditModalOpen}
        setEditValue={setEditValue}
        setProfileData={setProfileData}
      />
    );
  }

  if (currentStep === "success") {
    return (
      <FinalSuccessScreen
        questions={questions}
        answeredQuestions={answeredQuestions}
        onResetInterview={resetInterview}
      />
    );
  }

  console.log("Rendering main interview screen with current question:", profileSummary);
  return (
    <div className="from-gray-50 via-white to-blue-50 welcome-screen">
      <div className={`${!isFinalPhase ? 'grid grid-cols-1 lg:grid-cols-3' : ''} gap-8 max-w-8xl mx-auto`}>
        {/* Main Interview Area */}
        <div className="lg:col-span-2">
          <InterviewMainScreen
            currentQuestion={currentQuestion}
            currentInterviewData={latestInterviewData || currentInterviewData}
            isFollowup={isFollowup}
            interviewComplete={interviewComplete}
            allPhasesComplete={allPhasesComplete}
            summary={summary}
            summaryLoading={summaryLoading}
            confirming={confirming}
            phase={phase}
            sessionId={sessionId}
            loadingNextQuestion={loadingNextQuestion}
            isLoadingQuestionWithAudio={isLoadingQuestionWithAudio}
            isRecording={isRecording}
            showDocumentUpload={showDocumentUpload}
            uploadedDocuments={uploadedDocuments}
            elevenLabsError={elevenLabs.error}
            onAnswerChange={handleAnswerChange}
            onToggleRecording={toggleRecording}
            onToggleDocumentUpload={() => setShowDocumentUpload(!showDocumentUpload)}
            onDocumentUpload={handleDocumentUpload}
            onDocumentRemove={handleDocumentRemove}
            onNextQuestion={handleNextQuestion}
            onGenerateSummary={generateSummary}
            onSuccessScreenAction={handleSuccessScreenAction}
            onSummaryGenerated={handleSummaryGenerated}
            onReturningForInterview={returningForInterview}
            setPhase={setPhase}
            setConfirming={setConfirming}
            formatPhase={formatPhase}
            elevenLabsIsPlaying={elevenLabs.isPlaying}
            onMuteToggle={elevenLabs.stop}
            elevenLabs={elevenLabs}
            profileSummary={profileSummary}
            fetchProfileSummary={fetchProfileSummary}
          />
        </div>

        {/* Sidebar - Questions & Responses */}
        {!isFinalPhase && (
          <div className="lg:col-span-1">
            <QuestionsSidebar
              questionsAndResponses={questionsAndResponses}
              currentQuestion={currentQuestion}
              uploadedDocuments={uploadedDocuments}
              interviewComplete={interviewComplete}
              currentInterviewData={latestInterviewData || currentInterviewData}
              onQuestionClick={handleQuestionClick}
              onToggleQuestionCollapse={toggleQuestionCollapse}
            />
          </div>
        )}
      </div>

      {/* Summary Review Modal */}
      <SummaryReviewModal
        isOpen={showSummaryReview}
        onClose={handleCloseSummaryReview}
        summaries={phaseSummaries}
      />
    </div>
  );
};

export default WelcomeInterview;